<template>
  <div>
    <div class="relative w-fit" :class="!isShopFilter && 'lg:hidden'">
      <button
        id="filterDropdownTrigger"
        class="inline-flex h-12 items-center gap-3 rounded-full pl-6 pr-1 text-xl text-blue-800"
        :class="route.query.category ? 'bg-orange' : 'bg-light-gray'"
        @click="isActive = !isActive"
      >
        {{ $t('filters.filters') }} <template v-if="route.query.category">(1)</template>
        <div class="flex h-10 w-10 items-center justify-center rounded-full bg-blue-800">
          <Icon name="chevron" :color="'white'" size="xs" />
        </div>
      </button>
      <Transition name="slideX">
        <div
          v-if="isActive"
          class="fixed left-0 top-0 z-10 h-[100dvh] w-full bg-neutral-100 lg:w-[500px]"
          aria-labelledby="filterDropdownTrigger"
        >
          <div
            class="fixed left-0 top-0 -z-1 h-screen w-[200vw] bg-black bg-opacity-60"
            @click="isActive = false"
          />
          <div class="relative z-2 h-full overflow-auto bg-neutral-100 px-4 py-5 shadow-lg">
            <div class="relative z-1 flex flex-wrap items-center justify-between gap-3">
              <span class="text-3xl font-light text-blue">{{ $t('filters.filters') }}</span>
              <button @click="isActive = false">
                <Icon name="close" color="#314863" />
              </button>
            </div>
            <div
              class="mb-5 mt-[10px] h-[17px] w-full bg-[url(/assets/images/backgrounds/slashes-gray.png)] bg-repeat"
            />
            <div class="flex h-[calc(100%-100px)] flex-col justify-between">
              <ul class="flex flex-wrap gap-x-1 gap-y-4">
                <li>
                  <Button
                    :disabled="store.isLoading"
                    variant="outline"
                    :class="[selectedFilter ? '!border-light-gray' : '!bg-orange-300']"
                    @click="selectedFilter = ''"
                  >
                    {{ $t('articles.all') }}</Button
                  >
                </li>
                <li v-for="category in categories" :key="category.id">
                  <Button
                    :disabled="store.isLoading"
                    :variant="'outline'"
                    class="w-fit"
                    :class="[
                      selectedFilter !== (props.isShopFilter ? category.title : category.name)
                        ? '!border-light-gray'
                        : '!bg-orange hover:!bg-orange-300'
                    ]"
                    @click="handleFilterClick(props.isShopFilter ? category.title : category.name)"
                    >{{ props.isShopFilter ? category.title : category.name }}</Button
                  >
                </li>
              </ul>
              <div class="flex flex-col">
                <div
                  class="mb-4 mt-[10px] h-[17px] w-full bg-[url(/assets/images/backgrounds/slashes-gray.png)] bg-repeat"
                />
                <Button variant="solid" class="ml-auto w-fit" icon @click="handleShowProducts">{{
                  $t('filters.showProducts')
                }}</Button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
    <div v-if="!isShopFilter" class="mb-10 hidden gap-1 lg:flex lg:flex-wrap lg:gap-3">
      <Button
        :disabled="store.isLoading"
        :variant="'outline'"
        :class="[!isShowAllArticles ? '!border-light-gray' : '!bg-orange hover:!bg-orange-300']"
        :to="localePath($route.path)"
      >
        {{ $t('articles.all') }}</Button
      >
      <Button
        v-for="category in categories"
        :key="category.id"
        :disabled="store.isLoading"
        variant="outline"
        :to="
          localeRoute({
            path: $route.path,
            query: { category: props.isShopFilter ? category.title : category.name }
          })
        "
        :class="[
          route.query.category !== (props.isShopFilter ? category.title : category.name)
            ? '!border-light-gray'
            : '!bg-orange hover:!bg-orange-300'
        ]"
        >{{ props.isShopFilter ? category.title : category.name }}</Button
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import { useBlogStore } from '~/stores/BlogStore'
import { useShopStore } from '~/stores/ShopStore'
import type { CategoryRecord, ShopProductRecord } from '~/types/generated'

const localePath = useLocalePath()
const localeRoute = useLocaleRoute()
const { bodyLock, bodyUnlock } = useBodyLock()
const selectedFilter = ref('')
const router = useRouter()

const props = defineProps({
  categories: {
    type: Array as PropType<CategoryRecord[] | ShopProductRecord[]>
  },
  isShopFilter: {
    type: Boolean,
    default: false
  }
})

const store = props.isShopFilter ? useShopStore() : useBlogStore()
const route = useRoute()

const isShowAllArticles = ref(false)
const isActive = ref(false)

function handleFilterClick(queryName: string) {
  selectedFilter.value = queryName
}

function handleShowProducts() {
  isActive.value = false
  if (selectedFilter.value) {
    router.replace({ path: route.path, query: { category: selectedFilter.value } })
  } else {
    router.replace({ path: route.path })
  }
}

watch(
  () => route.query,
  () => {
    setShowAllArticles()
  }
)

watch(isActive, () => {
  isActive.value ? bodyLock() : bodyUnlock()
})

function setShowAllArticles() {
  if (route.query.category) {
    isShowAllArticles.value = false
  } else {
    isShowAllArticles.value = true
  }
}

onMounted(() => {
  setShowAllArticles()
})
</script>
